import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, TrendingUp, Search, Zap } from 'lucide-react';

interface DiagnosticoData {
  url: string;
  scores: {
    performance: number;
    seo: number;
    accessibility: number;
  };
  metrics: {
    fcp: string;
    lcp: string;
    cls: string;
    tbt: string;
    si: string;
  };
}

export default function DiagnosticoGratuito() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    url: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatorioData, setRelatorioData] = useState<DiagnosticoData | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.nome || formData.nome.length < 3) {
      setError('Nome deve ter pelo menos 3 caracteres');
      setLoading(false);
      return;
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Email inválido');
      setLoading(false);
      return;
    }

    if (!formData.url) {
      setError('URL do site é obrigatória');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.0.14:5250/api';
      const response = await fetch(`${apiUrl}/diagnostico/analisar.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao processar análise');
      }

      if (result.success) {
        setRelatorioData(result.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excelente';
    if (score >= 50) return 'Precisa Melhorar';
    return 'Crítico';
  };

  const resetForm = () => {
    setRelatorioData(null);
    setFormData({ nome: '', email: '', whatsapp: '', url: '' });
    setError(null);
  };

  if (relatorioData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Resultado da Análise</h1>
            <p className="text-gray-600">Análise completa de: <span className="font-semibold">{relatorioData.url}</span></p>
          </div>
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">Análise concluída! Enviamos um relatório detalhado para seu email.</AlertDescription>
          </Alert>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className={`border-2 ${getScoreColor(relatorioData.scores.performance)}`}>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4" />Performance</CardTitle></CardHeader>
              <CardContent><div className="text-4xl font-bold mb-1">{relatorioData.scores.performance}</div><p className="text-xs opacity-75">{getScoreLabel(relatorioData.scores.performance)}</p></CardContent>
            </Card>
            <Card className={`border-2 ${getScoreColor(relatorioData.scores.seo)}`}>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Search className="h-4 w-4" />SEO</CardTitle></CardHeader>
              <CardContent><div className="text-4xl font-bold mb-1">{relatorioData.scores.seo}</div><p className="text-xs opacity-75">{getScoreLabel(relatorioData.scores.seo)}</p></CardContent>
            </Card>
            <Card className={`border-2 ${getScoreColor(relatorioData.scores.accessibility)}`}>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><TrendingUp className="h-4 w-4" />Acessibilidade</CardTitle></CardHeader>
              <CardContent><div className="text-4xl font-bold mb-1">{relatorioData.scores.accessibility}</div><p className="text-xs opacity-75">{getScoreLabel(relatorioData.scores.accessibility)}</p></CardContent>
            </Card>
          </div>
          <Card className="mb-8">
            <CardHeader><CardTitle>⚡ Core Web Vitals</CardTitle><CardDescription>Métricas essenciais de experiência do usuário</CardDescription></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-l-4 border-blue-500 pl-4"><h4 className="font-semibold text-sm text-gray-700 mb-1">First Contentful Paint (FCP)</h4><p className="text-2xl font-bold text-gray-900 mb-2">{relatorioData.metrics.fcp}</p><p className="text-xs text-gray-600">Tempo até aparecer o primeiro conteúdo. Ideal: menos de 1.8s</p></div>
                <div className="border-l-4 border-purple-500 pl-4"><h4 className="font-semibold text-sm text-gray-700 mb-1">Largest Contentful Paint (LCP)</h4><p className="text-2xl font-bold text-gray-900 mb-2">{relatorioData.metrics.lcp}</p><p className="text-xs text-gray-600">Tempo até carregar elemento principal. Ideal: menos de 2.5s</p></div>
                <div className="border-l-4 border-green-500 pl-4"><h4 className="font-semibold text-sm text-gray-700 mb-1">Cumulative Layout Shift (CLS)</h4><p className="text-2xl font-bold text-gray-900 mb-2">{relatorioData.metrics.cls}</p><p className="text-xs text-gray-600">Estabilidade visual. Ideal: menos de 0.1</p></div>
                <div className="border-l-4 border-orange-500 pl-4"><h4 className="font-semibold text-sm text-gray-700 mb-1">Total Blocking Time (TBT)</h4><p className="text-2xl font-bold text-gray-900 mb-2">{relatorioData.metrics.tbt}</p><p className="text-xs text-gray-600">Tempo travado. Ideal: menos de 200ms</p></div>
                <div className="border-l-4 border-indigo-500 pl-4 md:col-span-2"><h4 className="font-semibold text-sm text-gray-700 mb-1">Speed Index</h4><p className="text-2xl font-bold text-gray-900 mb-2">{relatorioData.metrics.si}</p><p className="text-xs text-gray-600">Velocidade de carregamento visual. Ideal: menos de 3.4s</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 mb-8">
            <CardContent className="pt-6">
              <h3 className="text-2xl font-bold mb-3">Quer uma Análise Completa e Profunda?</h3>
              <p className="mb-6 text-blue-50">O <strong>Coddar Pulse</strong> oferece análise avançada com monitoramento contínuo, relatórios detalhados, recomendações personalizadas e muito mais.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => window.open('https://pulse.coddar.com.br', '_blank')}>Conhecer Coddar Pulse</Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" onClick={resetForm}>Analisar Outro Site</Button>
              </div>
            </CardContent>
          </Card>
          <p className="text-center text-xs text-gray-500 mt-6">Análise gerada usando Google PageSpeed Insights. Os resultados podem variar dependendo da conexão e localização.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Diagnóstico Gratuito de Performance</h1>
          <p className="text-lg text-gray-600">Descubra como seu site está performando e receba dicas de otimização</p>
        </div>
        <Card>
          <CardHeader><CardTitle>Analisar Meu Site</CardTitle><CardDescription>Preencha os dados abaixo para receber uma análise completa</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (<Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>)}
              <div className="space-y-2"><Label htmlFor="nome">Nome Completo *</Label><Input id="nome" name="nome" type="text" placeholder="Seu nome" value={formData.nome} onChange={handleChange} disabled={loading} required /></div>
              <div className="space-y-2"><Label htmlFor="email">Email *</Label><Input id="email" name="email" type="email" placeholder="seu@email.com" value={formData.email} onChange={handleChange} disabled={loading} required /></div>
              <div className="space-y-2"><Label htmlFor="whatsapp">WhatsApp (opcional)</Label><Input id="whatsapp" name="whatsapp" type="tel" placeholder="(00) 00000-0000" value={formData.whatsapp} onChange={handleChange} disabled={loading} /></div>
              <div className="space-y-2"><Label htmlFor="url">URL do Site *</Label><Input id="url" name="url" type="url" placeholder="https://seusite.com.br" value={formData.url} onChange={handleChange} disabled={loading} required /><p className="text-xs text-gray-500">Digite a URL completa do site que deseja analisar</p></div>
              <Button type="submit" className="w-full" size="lg" disabled={loading}>{loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analisando... (isso pode levar até 30 segundos)</>) : ('Analisar Agora (Grátis)')}</Button>
              <p className="text-xs text-center text-gray-500">Ao enviar, você concorda em receber o relatório por email</p>
            </form>
          </CardContent>
        </Card>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4"><div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3"><Zap className="h-6 w-6 text-blue-600" /></div><h3 className="font-semibold mb-1">Performance</h3><p className="text-sm text-gray-600">Análise completa de velocidade</p></div>
          <div className="text-center p-4"><div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"><Search className="h-6 w-6 text-green-600" /></div><h3 className="font-semibold mb-1">SEO</h3><p className="text-sm text-gray-600">Otimização para buscadores</p></div>
          <div className="text-center p-4"><div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3"><TrendingUp className="h-6 w-6 text-purple-600" /></div><h3 className="font-semibold mb-1">Acessibilidade</h3><p className="text-sm text-gray-600">Experiência para todos</p></div>
        </div>
      </div>
    </div>
  );
}
